// Cache local SÍNCRONA de la resolución por feature. El router la lee en render (0ms)
// para decidir la variante sin esperar red; el hook la hidrata en background.
//
// Keyed por commerce_id (id_commerce de localStorage). resetStore en login NO borra
// localStorage, así que la preferencia sobrevive al re-login del mismo comercio.

import type {
  EvacReason,
  FeatureMenuState,
  FeatureVariant,
  FeatureVersionCacheEntry,
  FeedbackCatalogOption,
  FeedbackOptionsCatalog,
  InviteSurface,
  ManualEntry,
} from './types';

const CACHE_PREFIX = 'plick:feature-version:v1:';

export function getActiveCommerceId(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage.getItem('id_commerce');
  } catch {
    return null;
  }
}

function cacheKey(commerceId: string, featureKey: string): string {
  return `${CACHE_PREFIX}${commerceId}:${featureKey}`;
}

function normalizeVariant(value: unknown): FeatureVariant {
  return value === 'treatment' ? 'treatment' : 'control';
}

function normalizeMenuState(value: unknown): FeatureMenuState | null {
  if (
    value === 'exploring' ||
    value === 'adopted' ||
    value === 'reverted_sticky' ||
    value === 'never_tried' ||
    value === 'served'
  ) {
    return value;
  }
  return null;
}

// Default conservador 'none': si el backend (cache viejo o degradado) no manda
// invite_surface, NO mostramos modal ni card intrusivos. El canal PULL (chip) ya no
// depende de invite_surface, sino de manual_entry.
function normalizeInviteSurface(value: unknown): InviteSurface {
  if (value === 'modal' || value === 'card' || value === 'none') {
    return value;
  }
  return 'none';
}

// Default conservador 'hidden': si el backend (cache viejo) no manda manual_entry, ocultamos
// el chip. El server siempre emite 'available'/'hidden'; este fallback solo cubre cache stale
// previo a la migración del contrato.
function normalizeManualEntry(value: unknown): ManualEntry {
  return value === 'available' ? 'available' : 'hidden';
}

// Default robusto 'iterating' (pausa temporal): si el backend no manda evac_reason, el aviso
// de ramp-down usa el mensaje tranquilizador en vez del de descontinuado. 'discontinued' solo
// se aplica cuando el backend lo emite explícitamente.
function normalizeEvacReason(value: unknown): EvacReason {
  return value === 'discontinued' ? 'discontinued' : 'iterating';
}

// Normalización DEFENSIVA del catálogo de chips per-experimento (feedback_options del GET).
// EXPORTADA para que el hook la reuse (misma validación en cache e hidratación).
// Default null = "usa los defaults de strings.ts". NUNCA lanza ni devuelve arrays malformados:
// filtra entradas inválidas, dedupea por value y CAPEA a 7 (el FE agrega 'Otro' = 8 total, §3).
export function normalizeFeedbackOptions(value: unknown): FeedbackOptionsCatalog | null {
  if (!value || typeof value !== 'object') return null;
  const clean = (list: unknown): FeedbackCatalogOption[] | undefined => {
    if (!Array.isArray(list)) return undefined;
    const seen = new Set<string>();
    const out = list
      .filter(
        (o): o is FeedbackCatalogOption =>
          Boolean(o) &&
          typeof o === 'object' &&
          typeof (o as FeedbackCatalogOption).value === 'string' &&
          (o as FeedbackCatalogOption).value.trim() !== '' &&
          typeof (o as FeedbackCatalogOption).label === 'string' &&
          (o as FeedbackCatalogOption).label.trim() !== '',
      )
      .filter((o) => o.value !== 'other') // 'Otro' lo agrega el FE, no el catálogo (§3)
      .filter((o) => (seen.has(o.value) ? false : (seen.add(o.value), true)))
      .slice(0, 7);
    return out.length > 0 ? out : undefined;
  };
  const revert = clean((value as Record<string, unknown>).revert);
  const permanence = clean((value as Record<string, unknown>).permanence);
  return revert || permanence ? { ...(revert && { revert }), ...(permanence && { permanence }) } : null;
}

// Lee la entrada cacheada. Devuelve null si no hay commerce activo o no hay cache.
// Nunca lanza: degradación segura → el caller decide el fallback (control).
export function readCache(featureKey: string, commerceId = getActiveCommerceId()): FeatureVersionCacheEntry | null {
  if (typeof window === 'undefined' || !commerceId) return null;
  try {
    const raw = window.localStorage.getItem(cacheKey(commerceId, featureKey));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<FeatureVersionCacheEntry>;
    return {
      feature_key: featureKey,
      variant: normalizeVariant(parsed.variant),
      can_revert: Boolean(parsed.can_revert),
      ga_forced: Boolean(parsed.ga_forced),
      menu_state: normalizeMenuState(parsed.menu_state),
      experiment_id: typeof parsed.experiment_id === 'string' ? parsed.experiment_id : null,
      invite_surface: normalizeInviteSurface(parsed.invite_surface),
      manual_entry: normalizeManualEntry(parsed.manual_entry),
      next_eligible_at: typeof parsed.next_eligible_at === 'string' ? parsed.next_eligible_at : null,
      operator_evacuated: Boolean(parsed.operator_evacuated),
      evac_reason: normalizeEvacReason(parsed.evac_reason),
      compare_active: Boolean(parsed.compare_active),
      feedback_options: normalizeFeedbackOptions(parsed.feedback_options),
      updated_at: typeof parsed.updated_at === 'number' ? parsed.updated_at : 0,
      // Default null (no confirmado): un cache previo a este contrato se trata como NO confirmado
      // por el backend, así la hidratación autoritativa lo reconcilia y sella confirmed_at.
      confirmed_at: typeof parsed.confirmed_at === 'number' ? parsed.confirmed_at : null,
    };
  } catch {
    return null;
  }
}

export function writeCache(entry: FeatureVersionCacheEntry, commerceId = getActiveCommerceId()): void {
  if (typeof window === 'undefined' || !commerceId) return;
  try {
    window.localStorage.setItem(cacheKey(commerceId, entry.feature_key), JSON.stringify(entry));
  } catch {
    // ignore quota / private-mode
  }
}

// Barre TODAS las entradas de variante cacheadas (cualquier comercio/feature) bajo CACHE_PREFIX.
// Aislamiento multi-tenant: lo llama el logout para que la variante de rollout del comercio que
// cierra sesión NO sobreviva en localStorage. Sin esto, el aislamiento del cliente dependería de
// que el backend nunca reúse un id_commerce: si lo reusara, el siguiente usuario en la misma
// pestaña heredaría la variante (V1/V2) y el estado de invitación del comercio anterior antes de
// que el GET autoritativo reconcilie. El prefijo es privado a este módulo (dueño del cache); el
// logout no toca localStorage directamente, igual que llama resetFeatureVersionStore() y no el
// singleton. Nunca lanza: degradación segura en modo privado / quota.
export function clearFeatureVersionCache(): void {
  if (typeof window === 'undefined') return;
  try {
    Object.keys(window.localStorage)
      .filter((k) => k.startsWith(CACHE_PREFIX))
      .forEach((k) => window.localStorage.removeItem(k));
  } catch {
    // ignore private-mode / acceso bloqueado
  }
}
